"use client";

import { useTranslations } from "next-intl";
import { Section } from "@/components/ui/Section";
import { Mail, Facebook } from "lucide-react";

export function Contact() {
  const t = useTranslations("Index");

  return (
    <Section
      id="contact"
      title={t("contact_title")}
      subtitle={t("contact_subtitle")}
      className="relative bg-(--bg-primary) pb-32 pt-12"
      contentClassName="px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Column: Heading & Context */}
          <div className="flex flex-col items-start text-left gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-outfit text-(--text-primary) leading-tight tracking-tight">
              {t("contact_question")}
            </h2>
            <p className="text-lg md:text-xl text-(--text-secondary) font-light max-w-lg leading-relaxed">
              {t("contact_description")}
            </p>
          </div>

          {/* Right Column: Actions (Static & Professional) */}
          <div className="flex flex-col gap-8 w-full">
            {/* Email */}
            <ContactLink
              href="mailto:contact@devdj.pl"
              label="contact@devdj.pl"
              subLabel={t("contact_email_address")}
              icon={<Mail className="w-8 h-8" />}
            />

            {/* Facebook */}
            <ContactLink
              href="https://fb.com/DevDJpl"
              label="fb.com/DevDJpl"
              subLabel={t("contact_social_profile")}
              icon={<Facebook className="w-8 h-8" />}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

function ContactLink({
  href,
  label,
  subLabel,
  icon,
}: {
  href: string;
  label: string;
  subLabel: string;
  icon: React.ReactNode;
}) {
  const isExternal = href.startsWith("http");

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="group flex items-center gap-6 p-6 rounded-2xl border border-(--border-subtle) bg-(--bg-card) hover:border-(--brand-primary) hover:shadow-lg transition-all duration-300"
    >
      {/* Icon Container - Always Circular & Uniform */}
      <div className="shrink-0 flex items-center justify-center w-16 h-16 rounded-full bg-(--bg-primary) text-(--text-primary) border border-(--border-subtle) group-hover:bg-(--brand-primary) group-hover:text-white group-hover:border-transparent transition-colors duration-300">
        {icon}
      </div>

      {/* Label Content */}
      <div className="flex flex-col">
        <span className="text-xl md:text-2xl font-bold font-outfit text-(--text-primary) group-hover:text-(--brand-primary) transition-colors duration-300 break-all">
          {label}
        </span>
        <span className="text-sm font-medium text-(--text-secondary) uppercase tracking-wider group-hover:text-(--text-primary) transition-colors duration-300">
          {subLabel}
        </span>
      </div>
    </a>
  );
}
